import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { Divider, Grid } from 'semantic-ui-react'

import {
  Accordion,
  Alert,
  Button,
  FormikCheckbox,
  FormikDropdown,
  FormikInput,
  getFieldName,
  GridRowColumn,
  Link,
  makeReqBoolSchema,
  makeReqStringSchema,
  Modal,
  Popup,
  Text,
} from '@/componentsV1/BaseComponents'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  AdminAnnualTaxFiling,
  getAdminUpdateAnnualTaxFilingFetchKey,
  updateAdminAnnualTaxFiling,
} from './adminAnnualTaxFilings.slice'
import {
  AnnualTaxFilingForm,
  TaxfyleJob,
  TaxfyleJobStatus,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { ExtensionReason } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import ExtensionPaymentsForm from './ExtensionPaymentsForm'
import TaxFilingFormUploadModal, {
  useTaxFilingFormUploadModal,
} from './TaxFilingFormUploadModal'
import { TaxfyleHistoryContent } from './TaxfyleJobHistoryModal'
import {
  selectIsAccountingOpsManager,
  selectIsTaxCoordinator,
} from '@/selectors/user.selectors'

const getExtensionFilingStatusForForm = (form?: AnnualTaxFilingForm) => {
  if (!form) {
    return 'Not applicable'
  } else if (!form.extensionFiledAt) {
    return 'Incomplete'
  } else if (form.extensionAccepted) {
    return `Accepted (${convertUtcToLocalDate(form.extensionFiledAt)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)})`
  } else {
    return `Rejected (${convertUtcToLocalDate(form.extensionFiledAt)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)})`
  }
}

interface ExtensionReasonFormProps {
  values: {
    isExtended: boolean
    extensionReason: ExtensionReason | null
    personalFiledExternally: boolean | null | undefined
    businessFiledExternally: boolean | null | undefined
  }
  filing: AdminAnnualTaxFiling
  hasPermissionToEdit: boolean
  businessFilingForm?: AnnualTaxFilingForm
  personalFilingForm?: AnnualTaxFilingForm
  disableExternalExtension: boolean
  extensionReasonOptions: Array<{
    key: string
    text: string
    value: string
  }>
  isExtensionCancelled: boolean
}

const ExtensionReasonForm = ({
  values,
  filing,
  hasPermissionToEdit,
  businessFilingForm,
  personalFilingForm,
  disableExternalExtension,
  extensionReasonOptions,
  isExtensionCancelled,
}: ExtensionReasonFormProps) => {
  return (
    <>
      {values.isExtended && (
        <GridRowColumn>
          <FormikDropdown
            fullWidth
            name={getFieldName<typeof values>('extensionReason')}
            placeholder={filing.extensionReason ?? 'Select an extension reason'}
            options={extensionReasonOptions}
            label="Extension Reason"
            schema={makeReqStringSchema({
              field: 'extension reason',
              required: values.isExtended,
            })}
            disabled={!values.isExtended || !hasPermissionToEdit}
            afterLabel={
              isExtensionCancelled && (
                <Popup content="Since the extension job was canceled, the only allowed action is to mark the extension as filed externally." />
              )
            }
            shouldClearOnDismount
          />
        </GridRowColumn>
      )}
      {disableExternalExtension && (
        <GridRowColumn>
          <Alert type="warn">
            No business or personal filing form found for this user. A filing
            form must be created before extending externally.
          </Alert>
        </GridRowColumn>
      )}
      {businessFilingForm &&
        values.extensionReason === ExtensionReason.filedExternally && (
          <GridRowColumn>
            <FormikCheckbox
              name={getFieldName<typeof values>('businessFiledExternally')}
              label="Business extension filed externally"
              disabled={!values.isExtended || !hasPermissionToEdit}
              schema={
                values.personalFiledExternally !== true
                  ? makeReqBoolSchema({ trueOnly: true })
                  : undefined
              }
              shouldClearOnDismount
            />
          </GridRowColumn>
        )}
      {personalFilingForm &&
        values.extensionReason === ExtensionReason.filedExternally && (
          <GridRowColumn>
            <FormikCheckbox
              name={getFieldName<typeof values>('personalFiledExternally')}
              label="Personal extension filed externally"
              disabled={!values.isExtended || !hasPermissionToEdit}
              schema={
                values.businessFiledExternally !== true
                  ? makeReqBoolSchema({ trueOnly: true })
                  : undefined
              }
              shouldClearOnDismount
            />
          </GridRowColumn>
        )}
    </>
  )
}

const AdminExtensionInfoModal = ({
  filing,
  filingForms = [],
  extensionTaxfyleJob,
  refreshData,
  open,
  close,
}: {
  filing: AdminAnnualTaxFiling
  filingForms?: AnnualTaxFilingForm[]
  extensionTaxfyleJob?: TaxfyleJob | null
  refreshData: () => void
  open: boolean
  close: () => void
}) => {
  const isAccountingOpsManager = useReselector(selectIsAccountingOpsManager)
  const isTaxOpsManager = useReselector(selectIsTaxCoordinator)
  const hasPermissionToEdit = useMemo(
    () => isAccountingOpsManager || isTaxOpsManager,
    [isAccountingOpsManager, isTaxOpsManager]
  )
  const user = filing.user || filing.annualTaxFilings
  const isExtensionCancelled = useMemo(
    () => extensionTaxfyleJob?.jobStatus === TaxfyleJobStatus.canceled,
    [extensionTaxfyleJob]
  )
  const doesNotHaveInProgressExtensionJob = useMemo(
    () =>
      !filing.optedOutAt &&
      (!extensionTaxfyleJob ||
        TaxfyleJobStatus.underConstruction === extensionTaxfyleJob.jobStatus),
    [extensionTaxfyleJob, filing.optedOutAt]
  )
  const error = useReselector(
    getFetchError,
    getAdminUpdateAnnualTaxFilingFetchKey(filing.id)
  )
  const dispatch = useAppDispatch()
  const businessFilingForm = useMemo(
    () =>
      filingForms.find(
        (form) => form.formType.name === TAX_ENTITY_TYPES.form_1120_s
      ),
    [filingForms]
  )
  const personalFilingForm = useMemo(
    () =>
      filingForms.find(
        (form) => form.formType.name === TAX_ENTITY_TYPES.form_1040
      ),
    [filingForms]
  )

  const businessExtensionFilingStatus = useMemo(
    () => getExtensionFilingStatusForForm(businessFilingForm),
    [businessFilingForm]
  )
  const personalExtensionFilingStatus = useMemo(
    () => getExtensionFilingStatusForForm(personalFilingForm),
    [personalFilingForm]
  )

  const {
    modalOpen,
    setModalOpen,
    modalView,
    setModalView,
    modalUserContext,
    modalFilingFormIdContext,
    modalFilingFormTypeNameContext,
    modalFilingFormExtensionPaymentsContext,
    filingFormIsExtendedContext,
    closeModal,
    handleModalOpen,
  } = useTaxFilingFormUploadModal()

  const initialValues = useMemo(
    () => ({
      isExtended: Boolean(filing.extensionRequestedAt),
      extensionReason: filing.extensionReason,
      isExempted: Boolean(filing.exemptedFromExtensionAt),
      exemptionReason: filing.exemptedFromExtensionReason,
      businessFiledExternally:
        businessFilingForm?.extensionFiledExternally ?? null,
      personalFiledExternally:
        personalFilingForm?.extensionFiledExternally ?? null,
    }),
    [
      businessFilingForm?.extensionFiledExternally,
      filing.exemptedFromExtensionAt,
      filing.exemptedFromExtensionReason,
      filing.extensionReason,
      filing.extensionRequestedAt,
      personalFilingForm?.extensionFiledExternally,
    ]
  )

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      if (!hasPermissionToEdit) {
        close()
        return false
      }
      const updates: Partial<AdminAnnualTaxFiling> & {
        personalExtensionFiledExternally?: boolean | null
        businessExtensionFiledExternally?: boolean | null
      } = {}
      // only update if values changed
      if (values.isExtended !== initialValues.isExtended) {
        updates.extensionRequestedAt = values.isExtended
          ? new Date().toISOString()
          : null
      }

      // The extension reason and filed externally flags should change at the same time
      if (
        values.extensionReason !== initialValues.extensionReason ||
        values.businessFiledExternally !== initialValues.businessFiledExternally
      ) {
        updates.extensionReason = values.isExtended
          ? values.extensionReason
          : null
        updates.businessExtensionFiledExternally =
          values.businessFiledExternally
      }

      if (
        values.extensionReason !== initialValues.extensionReason ||
        values.personalFiledExternally !== initialValues.personalFiledExternally
      ) {
        updates.extensionReason = values.isExtended
          ? values.extensionReason
          : null
        updates.personalExtensionFiledExternally =
          values.personalFiledExternally
      }

      if (values.isExempted !== initialValues.isExempted) {
        updates.exemptedFromExtensionAt = values.isExempted
          ? new Date().toISOString()
          : null
        updates.exemptedFromExtensionReason = values.isExempted
          ? values.exemptionReason
          : null
      }

      if (Object.keys(updates).length > 0) {
        const res = await updateAdminAnnualTaxFiling(
          filing.id,
          updates
        )(dispatch)
        if (res) {
          refreshData()
          return true
        }
      }
      return false
    },
  })

  const { values, submitForm, isSubmitting, isValid } = formik

  const extensionReasonOptions = useMemo(
    () =>
      Object.values(ExtensionReason)
        .map((reason) => ({
          key: reason,
          text: reason,
          value: reason,
        }))
        .filter((reason) => {
          if (filing.extensionReason) {
            return reason.value === ExtensionReason.filedExternally
          }
          return [
            ExtensionReason.lateJoiner,
            ExtensionReason.tskOverdue,
            ExtensionReason.taxEntityUnconfirmed,
            ExtensionReason.bookkeepingTasksIncomplete,
            ExtensionReason.booksNotLocked,
            ExtensionReason.taxQuestionnaireOverdue,
            ExtensionReason.filedExternally,
          ].includes(reason.value)
        }),
    [filing.extensionReason]
  )

  const extensionSurveyStatus = useMemo(() => {
    if (!filing.extensionRequestedAt) {
      return null
    } else if (!filing.extensionStartedAt) {
      return 'Not Started'
    } else if (filing.extensionRequestSurveyCompletedAt) {
      return `Completed (${convertUtcToLocalDate(filing.extensionRequestSurveyCompletedAt)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)})`
    } else {
      return `In Progress (${convertUtcToLocalDate(filing.extensionStartedAt)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)})`
    }
  }, [
    filing.extensionRequestedAt,
    filing.extensionStartedAt,
    filing.extensionRequestSurveyCompletedAt,
  ])

  const disableExternalExtension = useMemo(() => {
    return (
      values.extensionReason === ExtensionReason.filedExternally &&
      !businessFilingForm &&
      !personalFilingForm
    )
  }, [values.extensionReason, businessFilingForm, personalFilingForm])

  return (
    <Modal size="small" open={open} onClose={close} closeIcon>
      <Modal.Header>
        <Text as="h1">
          Extension Information for{' '}
          {`${user?.firstName} ${user?.lastName} (${user?.id})`}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Grid>
          {filing.optedOutAt && (
            <GridRowColumn>
              <Alert>This user is opted out of taxes</Alert>
            </GridRowColumn>
          )}
          {!hasPermissionToEdit && (
            <GridRowColumn>
              <Alert>
                <Text>
                  You do not have permission to edit this user&apos;s extension
                  information. Please contact an Accounting Ops or Tax Ops
                  manager to make changes.
                </Text>
              </Alert>
            </GridRowColumn>
          )}
          <FormikProvider value={formik}>
            {doesNotHaveInProgressExtensionJob && (
              <>
                <GridRowColumn>
                  <FormikCheckbox
                    name={getFieldName<typeof formik.values>('isExtended')}
                    label={
                      <Text>
                        Is extended?
                        <Popup content="Puts the user into the extension flow." />
                      </Text>
                    }
                    toggle
                    disabled={!hasPermissionToEdit}
                    onChange={(value) => {
                      if (value) {
                        formik.setFieldValue(
                          getFieldName<typeof formik.values>('isExempted'),
                          false
                        )
                      }
                    }}
                  />
                </GridRowColumn>
                <ExtensionReasonForm
                  values={values}
                  filing={filing}
                  hasPermissionToEdit={hasPermissionToEdit}
                  businessFilingForm={businessFilingForm}
                  personalFilingForm={personalFilingForm}
                  disableExternalExtension={disableExternalExtension}
                  extensionReasonOptions={extensionReasonOptions}
                  isExtensionCancelled={isExtensionCancelled}
                />
                <GridRowColumn>
                  <FormikCheckbox
                    name={getFieldName<typeof formik.values>('isExempted')}
                    label={
                      <Text>
                        Exempt from forced extensions?
                        <Popup content="Exempts the user from being forced into the extension flow. This will be turned off automatically for the next round of forced extensions." />
                      </Text>
                    }
                    toggle
                    disabled={values.isExtended || !hasPermissionToEdit}
                  />
                </GridRowColumn>
                {values.isExempted && (
                  <GridRowColumn>
                    <FormikInput
                      name={getFieldName<typeof formik.values>(
                        'exemptionReason'
                      )}
                      label="Exemption Reason"
                      disabled={!values.isExempted || !hasPermissionToEdit}
                      schema={makeReqStringSchema({
                        field: 'exemption reason',
                        required: values.isExempted,
                      })}
                      shouldClearOnDismount
                    />
                  </GridRowColumn>
                )}
              </>
            )}
            {filing.extensionRequestedAt && (
              <>
                <GridRowColumn>
                  <Text as="h3">Extension Requested</Text>
                </GridRowColumn>
                <GridRowColumn short>
                  <Text>
                    {filing.extensionRequestedAt
                      ? `Yes (${convertUtcToLocalDate(filing.extensionRequestedAt)?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)})`
                      : 'No'}
                  </Text>
                </GridRowColumn>
                {!doesNotHaveInProgressExtensionJob && (
                  <ExtensionReasonForm
                    values={values}
                    filing={filing}
                    hasPermissionToEdit={hasPermissionToEdit}
                    businessFilingForm={businessFilingForm}
                    personalFilingForm={personalFilingForm}
                    disableExternalExtension={disableExternalExtension}
                    extensionReasonOptions={extensionReasonOptions}
                    isExtensionCancelled={isExtensionCancelled}
                  />
                )}
                <GridRowColumn>
                  <Text as="h3">Extension Survey Status</Text>
                </GridRowColumn>
                <GridRowColumn short>
                  <Text>{extensionSurveyStatus}</Text>
                </GridRowColumn>
                <Divider />
                <GridRowColumn>
                  <Text as="h3">Taxfyle Job</Text>
                </GridRowColumn>
                <GridRowColumn short>
                  {!extensionTaxfyleJob && <Text>Not available</Text>}
                  {extensionTaxfyleJob && (
                    <Link
                      href={`${process.env.VITE_TAXFYLE_BASE_URL}/jobs/${extensionTaxfyleJob.jobId}`}
                      newPage
                    >
                      Job portal
                    </Link>
                  )}
                </GridRowColumn>
                {extensionTaxfyleJob && (
                  <>
                    <GridRowColumn>
                      <Accordion
                        variant="text"
                        title="View Activity"
                        content={
                          <TaxfyleHistoryContent
                            taxfyleJobId={extensionTaxfyleJob.jobId}
                            createdAt={extensionTaxfyleJob.createdAt}
                            open={open}
                          />
                        }
                      />
                    </GridRowColumn>
                    <GridRowColumn>
                      <Text as="h3">Job Status</Text>
                    </GridRowColumn>
                    <GridRowColumn short>
                      <Text>{extensionTaxfyleJob.jobStatus}</Text>
                    </GridRowColumn>
                    <GridRowColumn>
                      <Text as="h3">Job Step</Text>
                    </GridRowColumn>
                    <GridRowColumn short>
                      <Text>
                        {extensionTaxfyleJob.jobStep ?? 'No Job Step'}
                      </Text>
                    </GridRowColumn>
                  </>
                )}
                <Divider />
                <GridRowColumn>
                  <Text as="h3">Business Extension</Text>
                </GridRowColumn>
                <GridRowColumn short>
                  <Text>{businessExtensionFilingStatus}</Text>
                </GridRowColumn>
                <GridRowColumn>
                  <Divider />
                </GridRowColumn>
                <GridRowColumn>
                  <Text as="h3">Personal Extension</Text>
                </GridRowColumn>
                <GridRowColumn short>
                  <Text>{personalExtensionFilingStatus}</Text>
                </GridRowColumn>
                {personalFilingForm?.extensionAccepted && (
                  <>
                    <GridRowColumn>
                      <Text as="h3">Personal Extension Payments</Text>
                    </GridRowColumn>
                    <GridRowColumn>
                      <ExtensionPaymentsForm
                        key={`${personalFilingForm.id}-extension-payments-form`}
                        paymentInfo={personalFilingForm.extensionPayments}
                        isEditing={false}
                        userId={personalFilingForm.userId}
                        filingFormId={personalFilingForm.id}
                        filingFormType={personalFilingForm.formType.name}
                        closeModal={closeModal}
                        isFormExtended={Boolean(filing.extensionRequestedAt)}
                      />
                    </GridRowColumn>
                    <GridRowColumn>
                      <Button
                        variant="link"
                        onClick={() =>
                          handleModalOpen(
                            'Extension Information',
                            `${user?.firstName} ${user?.lastName}`,
                            personalFilingForm.id,
                            personalFilingForm.formType.name,
                            personalFilingForm.extensionPayments,
                            Boolean(filing.extensionRequestedAt)
                          )
                        }
                      >
                        Update payment
                      </Button>
                    </GridRowColumn>
                  </>
                )}
              </>
            )}
          </FormikProvider>
          {user && (
            <TaxFilingFormUploadModal
              modalOpen={modalOpen}
              closeModal={() => setModalOpen(false)}
              modalView={modalView}
              setModalView={setModalView}
              modalUserContext={modalUserContext}
              filingId={filing.id}
              refreshData={refreshData}
              filingFormId={modalFilingFormIdContext}
              filingFormTypeName={modalFilingFormTypeNameContext}
              taxFilingUserId={user.id}
              filingFormExtensionPayments={
                modalFilingFormExtensionPaymentsContext
              }
              filingFormIsExtended={filingFormIsExtendedContext}
            />
          )}
          {error && (
            <GridRowColumn>
              <Alert type="error">{error.message}</Alert>
            </GridRowColumn>
          )}
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={close}
          variant="actionLink"
          style={{ marginRight: 32 }}
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitting || !isValid || disableExternalExtension}
          onClick={submitForm}
        >
          {hasPermissionToEdit ? 'Update' : 'Close'}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default AdminExtensionInfoModal
